import { useContext, useEffect, useMemo, useState } from 'react';
import { FeatureFlagContext } from '../contexts/FeatureFlagContext';
import { PaymentCategoryOption, Beneficiary } from 'constants/beneficiary.constants';
import { useAppSelector } from './redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { createCategoryMap, normalizePaymentCategories, formatPaymentCategory } from 'utils/paymentCategoryUtils';

export const useFeatureFlag = (flagName: string): boolean => {
	const { featureFlags, isLoading } = useContext(FeatureFlagContext);

	if (isLoading) {
		return false;
	}

	if (featureFlags[flagName] === null) {
		return true;
	}

	return featureFlags[flagName];
};

/**
 * Formats a payment category for display based on beneficiary's categories and payment reason ID.
 * Returns a formatted string like "Primary / Secondary / Tertiary" or empty string if not found.
 */
export const usePaymentCategoryDisplay = (beneficiary?: Beneficiary, paymentReasonId?: string) => {
	const [categoryOptions, setCategoryOptions] = useState<PaymentCategoryOption[]>([]);
	const t = useAppSelector(selectTranslations);

	useEffect(() => {
		const normalized = normalizePaymentCategories(beneficiary?.paymentCategories);
		setCategoryOptions(normalized);
	}, [beneficiary?.paymentCategories]);

	const categoryMap = useMemo(() => createCategoryMap(categoryOptions), [categoryOptions]);

	const paymentCategoryDisplay = useMemo(() => {
		if (!paymentReasonId) return '';
		const option = categoryMap[paymentReasonId];
		return formatPaymentCategory(option, paymentReasonId, t);
	}, [paymentReasonId, categoryMap, t]);

	return paymentCategoryDisplay;
};
